/**
 * Error Handling Utilities
 *
 * Standardized error extraction and handling across the application.
 * These utilities ensure consistent error messages from various sources:
 * - Axios errors (with Strapi error format)
 * - JavaScript Error objects
 * - Unknown error types
 *
 * @example
 * import { extractErrorMessage, isNetworkError, ApiError } from '@spcc/core'
 *
 * try {
 *   await api.createOrder(data)
 * } catch (error) {
 *   const message = extractErrorMessage(error)
 *   // Returns user-friendly message
 * }
 */

import type { AxiosError } from 'axios'

// ============================================================================
// Types
// ============================================================================

/**
 * Strapi v5 error response format
 */
export interface StrapiErrorResponse {
  data: null
  error: {
    status: number
    name: string
    message: string
    details?: Record<string, unknown>
  }
}

/**
 * Strapi validation error details
 */
export interface StrapiValidationError {
  path: string[]
  message: string
  name: string
}

/**
 * Custom API Error with additional context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ============================================================================
// Error Type Guards
// ============================================================================

/**
 * Check if error is an Axios error
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}

/**
 * Check if error response has Strapi error format
 */
export function isStrapiError(data: unknown): data is StrapiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as StrapiErrorResponse).error === 'object' &&
    typeof (data as StrapiErrorResponse).error?.message === 'string'
  )
}

/**
 * Check if error is a network error (no response)
 */
export function isNetworkError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.code === 'ERR_NETWORK' || !error.response
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
}

/**
 * Check if error is an authentication error (401)
 */
export function isAuthError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.response?.status === 401
}

/**
 * Check if error is a forbidden error (403)
 */
export function isForbiddenError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.response?.status === 403
}

/**
 * Check if error is a not found error (404)
 */
export function isNotFoundError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.response?.status === 404
}

/**
 * Check if error is a validation error (400)
 */
export function isValidationError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.response?.status === 400
}

/**
 * Check if error is a conflict error (409)
 */
export function isConflictError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  return error.response?.status === 409
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  const status = error.response?.status
  return status !== undefined && status >= 500 && status < 600
}

// ============================================================================
// Error Message Extraction
// ============================================================================

/**
 * Default error messages for common scenarios
 */
export const DEFAULT_ERROR_MESSAGES = {
  NETWORK: 'errors.network',
  TIMEOUT: 'errors.timeout',
  AUTH: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  NOT_FOUND: 'errors.notFound',
  VALIDATION: 'errors.validation',
  CONFLICT: 'errors.conflict',
  SERVER: 'errors.server',
  UNKNOWN: 'errors.unknown',
} as const

/**
 * Extract a user-friendly error message from any error type
 *
 * Priority:
 * 1. Strapi error message (error.response.data.error.message)
 * 2. Axios error message
 * 3. Error.message
 * 4. Default message based on error type
 * 5. Generic fallback
 *
 * @param error - The error to extract message from
 * @param fallback - Optional fallback message (i18n key recommended)
 * @returns User-friendly error message or i18n key
 */
export function extractErrorMessage(
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGES.UNKNOWN
): string {
  // Handle null/undefined
  if (error === null || error === undefined) {
    return fallback
  }

  // Handle Axios errors
  if (isAxiosError(error)) {
    // Check for Strapi error format first
    const data = error.response?.data
    if (isStrapiError(data)) {
      return data.error.message
    }

    // Check for other API error formats
    if (typeof data === 'object' && data !== null) {
      // Format: { message: string }
      if ('message' in data && typeof (data as { message: string }).message === 'string') {
        return (data as { message: string }).message
      }
      // Format: { error: string }
      if ('error' in data && typeof (data as { error: string }).error === 'string') {
        return (data as { error: string }).error
      }
    }

    // Return default messages for common HTTP errors
    if (isNetworkError(error)) return DEFAULT_ERROR_MESSAGES.NETWORK
    if (isTimeoutError(error)) return DEFAULT_ERROR_MESSAGES.TIMEOUT
    if (isAuthError(error)) return DEFAULT_ERROR_MESSAGES.AUTH
    if (isForbiddenError(error)) return DEFAULT_ERROR_MESSAGES.FORBIDDEN
    if (isNotFoundError(error)) return DEFAULT_ERROR_MESSAGES.NOT_FOUND
    if (isValidationError(error)) return DEFAULT_ERROR_MESSAGES.VALIDATION
    if (isConflictError(error)) return DEFAULT_ERROR_MESSAGES.CONFLICT
    if (isServerError(error)) return DEFAULT_ERROR_MESSAGES.SERVER

    // Fallback to Axios error message
    if (error.message) return error.message
  }

  // Handle ApiError
  if (error instanceof ApiError) {
    return error.message
  }

  // Handle standard Error
  if (error instanceof Error) {
    return error.message
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error
  }

  // Unknown error type
  return fallback
}

/**
 * Extract validation errors from Strapi response
 *
 * @param error - The error to extract validation errors from
 * @returns Array of validation errors with field paths
 */
export function extractValidationErrors(error: unknown): StrapiValidationError[] {
  if (!isAxiosError(error) || !error.response?.data) {
    return []
  }

  const data = error.response.data
  if (!isStrapiError(data)) {
    return []
  }

  const details = data.error.details
  if (!details || typeof details !== 'object') {
    return []
  }

  // Strapi validation errors are in details.errors
  if ('errors' in details && Array.isArray(details.errors)) {
    return details.errors as StrapiValidationError[]
  }

  return []
}

/**
 * Extract the `error.details` payload of a Strapi error response.
 *
 * Strapi puts anything a service passes as the second ValidationError argument
 * here, which is how an API returns structured context alongside a failure.
 *
 * @param error - The error to extract details from
 * @returns The details object, or null when the error carries none
 */
export function extractErrorDetails(
  error: unknown
): Record<string, unknown> | null {
  // The shared HTTP client rejects with an ApiError that already carries them.
  if (error instanceof ApiError) {
    return error.details ?? null
  }

  if (!isAxiosError(error) || !error.response?.data) {
    return null
  }

  const data = error.response.data
  if (!isStrapiError(data)) {
    return null
  }

  const details = data.error.details
  return details && typeof details === 'object' ? details : null
}

/**
 * Get HTTP status code from error
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (isAxiosError(error)) {
    return error.response?.status
  }
  if (error instanceof ApiError) {
    return error.status
  }
  return undefined
}

/**
 * Create an ApiError from any error type
 */
export function toApiError(error: unknown): ApiError {
  const message = extractErrorMessage(error)
  const status = getErrorStatus(error)

  if (error instanceof ApiError) {
    return error
  }

  return new ApiError(message, status)
}
